import { PropositionProductType, templateTypes } from "@dune/vars";
import { contractDocumentTypeEnum } from "../contracts";
import { PropositionDocumentType, propositionDocumentTypeEnum } from "../propositions";

export type DocumentDataSource = {
	type: "relation" | "override" | "computed" | "static";
	config: Record<string, unknown>;
};

export type DocumentSchema = {
	prismaModel: string;
	fields: string[];
	computedFields?: string[];
	labels?: Record<string, string>;
	entityType?: "main" | "relation";
	hidden?: boolean;
	relations?: {
		[key: string]: {
			model: string;
			field?: string;
			label?: string;
			filter?: Record<string, string | number | boolean>;
			orderBy?: Record<string, "asc" | "desc">;
			first?: boolean;
		};
	};
	dataSources?: Record<string, DocumentDataSource>;
	tableDataSources?: {
		[key: string]: TableDataSource;
	};
	dataSourceFields?: Record<
		string,
		{
			fields: string[];
			labels: Record<string, string>;
			model: string;
			label: string;
		}
	>;
};

export type StaticTag = {
	key: string;
	label: string;
	value: string;
};

export type ParameterizedTag = {
	key: string;
	label: string;
	description: string;
	entityType: string;
	relationName: string;
	field: string;
	model: string;
};

export type ComputedFieldConfig = {
	label: string;
	generator: (entityId: number) => Promise<string>;
};

export type TableDataSource = {
	label: string;
	description: string;
	model: string;
	type: "table";
	computedFields?: Record<string, { label: string }>;
};

export type DocumentMappingsResponse = {
	documentMappings: Record<string, DocumentSchema>;
	staticTags: StaticTag[];
	computedFields: Record<string, Record<string, ComputedFieldConfig>>;
	relations: Record<string, Record<string, { label: string; model: string }>>;
	parameterizedTags: Record<string, ParameterizedTag>;
	tableDataSources: Record<string, Record<string, TableDataSource>>;
};

export const DocumentTemplateType = {
	CONVENTION_APPORTEUR: 'CONVENTION_APPORTEUR',
	CONTRAT_STANDARD: 'CONTRAT_STANDARD',
	RAPPORT_EXPERTISE: 'RAPPORT_EXPERTISE',
	DEVIS: 'DEVIS',
	DEVIS_GFA: 'DEVIS_GFA',
	RAPPORT_EXPERTISE_GFA: 'RAPPORT_EXPERTISE_GFA',
	PROPOSITION_ASSURANCE_DO: 'PROPOSITION_ASSURANCE_DO',
	PROPOSITION_ASSURANCE_TRC: 'PROPOSITION_ASSURANCE_TRC',
	PROPOSITION_ASSURANCE_MRT: 'PROPOSITION_ASSURANCE_MRT',
	PROPOSITION_ASSURANCE_GFA: 'PROPOSITION_ASSURANCE_GFA',
	CONDITIONS_GENERALES_DO: 'CONDITIONS_GENERALES_DO',
	CONDITIONS_GENERALES_TRC: 'CONDITIONS_GENERALES_TRC',
	CONDITIONS_GENERALES_MRT: 'CONDITIONS_GENERALES_MRT',
	CONDITIONS_GENERALES_GFA: 'CONDITIONS_GENERALES_GFA',
	INDICATION_TARIFAIRE_DO_AXERIA: 'INDICATION_TARIFAIRE_DO_AXERIA',
	INDICATION_TARIFAIRE_TRC_AXERIA: 'INDICATION_TARIFAIRE_TRC_AXERIA',
	INDICATION_TARIFAIRE_DO_CARAVELA: 'INDICATION_TARIFAIRE_DO_CARAVELA',
	INDICATION_TARIFAIRE_TRC_CARAVELA: 'INDICATION_TARIFAIRE_TRC_CARAVELA',
	INDICATION_TARIFAIRE_MRT_ZURICH: 'INDICATION_TARIFAIRE_MRT_ZURICH',
	INDICATION_TARIFAIRE_GFA_CARAVELA: 'INDICATION_TARIFAIRE_GFA_CARAVELA',
	QUITTANCE: 'QUITTANCE',
	CONTRAT_ASSURANCE_DO: 'CONTRAT_ASSURANCE_DO',
	CONTRAT_ASSURANCE_TRC: 'CONTRAT_ASSURANCE_TRC',
	CONTRAT_ASSURANCE_MRT: 'CONTRAT_ASSURANCE_MRT',
	CONTRAT_ASSURANCE_GFA: 'CONTRAT_ASSURANCE_GFA',
	ATTESTATION_DEFINITIVE_DO: 'ATTESTATION_DEFINITIVE_DO',
	ATTESTATION_DEFINITIVE_TRC: 'ATTESTATION_DEFINITIVE_TRC',
	ATTESTATION_DEFINITIVE_MRT: 'ATTESTATION_DEFINITIVE_MRT',
	ATTESTATION_DEFINITIVE_GFA: 'ATTESTATION_DEFINITIVE_GFA',
	AVENANT_FIN_DE_TRAVAUX_DO: 'AVENANT_FIN_DE_TRAVAUX_DO',
	AVENANT_FIN_DE_TRAVAUX_TRC: 'AVENANT_FIN_DE_TRAVAUX_TRC',
	AVENANT_FIN_DE_TRAVAUX_MRT: 'AVENANT_FIN_DE_TRAVAUX_MRT',
	AVENANT_FIN_DE_TRAVAUX_GFA: 'AVENANT_FIN_DE_TRAVAUX_GFA',
	QUITTANCEMENT_RECEPTION_DO: 'QUITTANCEMENT_RECEPTION_DO',
	QUITTANCEMENT_RECEPTION_TRC: 'QUITTANCEMENT_RECEPTION_TRC',
	QUITTANCEMENT_RECEPTION_MRT: 'QUITTANCEMENT_RECEPTION_MRT',
	QUITTANCEMENT_RECEPTION_GFA: 'QUITTANCEMENT_RECEPTION_GFA',
} as const;

export type DocumentTemplateTypeValues = typeof DocumentTemplateType[keyof typeof DocumentTemplateType];

export const EntityModel = {
	USER: "USER",
	SOCIETY: "SOCIETY",
	CONSTRUCTION_SITE: "CONSTRUCTION_SITE",
	PROPOSITION: "PROPOSITION",
	QUITTANCE: "QUITTANCE",
	GFA_CONSTRUCTION_SITE: "GFA_CONSTRUCTION_SITE",
} as const;

export type EntityModelValues = typeof EntityModel[keyof typeof EntityModel];

export type DocumentTemplate = {
	id: string;
	name: string;
	entityModel: EntityModelValues;
	templateType: DocumentTemplateTypeValues;
	fileId: string;
	version: number;
	validated: boolean;
	invalidFields: string[] | null;
	fields: string[];
	labels: Record<string, string> | null;
	createdAt: string;
	updatedAt: string;
	file?: {
		name: string;
		mimeType: string;
	};
};

export interface GenerateConventionDto {
	societyId: string;
	templateId: string;
	signataireDuneId?: string;
}

export interface SendConventionRequest {
	societyId: number;
	signatureMethod: SignatureMethodValues;
	fileId?: string;
}

export interface SendConventionResponse {
	message: string;
	envelopeId?: string;
	trackingId?: string;
	taskId?: string;
	recipients?: {
		broker: string;
		dune: string;
	};
}

export const ProjectDocumentForSignatureType = {
	PROPOSITION: "proposition",
	CONTRACT: "contract",
	END_OF_WORK_AMENDMENT: "end-of-work-amendment",
} as const;
export type ProjectDocumentForSignatureTypeKeys = keyof typeof ProjectDocumentForSignatureType;

export const ProjectDocumentGenerationType = {
	PROPOSITION: "proposition",
	CONTRACT: "contract",
	END_OF_WORK_AMENDMENT: "avenant fin de travaux",
	FINAL_CERTIFICATE: "attestation définitive",
	QUITTANCE: "quittance",
} as const;

export type ProjectDocumentGenerationTypeKeys = keyof typeof ProjectDocumentGenerationType;

export const GFAProjectDocumentGenerationType = {
	...ProjectDocumentGenerationType,
	DEVIS_GFA: "devis gfa",
	RAPPORT_EXPERTISE_GFA: "rapport expertise gfa",
	INDICATION_TARIFAIRE_GFA_CARAVELA: "indication tarifaire gfa caravela",
} as const;

export type GFAProjectDocumentGenerationTypeKeys = keyof typeof GFAProjectDocumentGenerationType;

export interface SendDocumentForSignatureRequest {
	propositionId: number;
	signatureMethod: SignatureMethodValues;
	fileId: string;
	documentType: ProjectDocumentForSignatureTypeKeys;
	signataireDuneId?: number;
	signatoryEmail?: string;
	signatoryFirstname?: string;
	signatoryLastname?: string;
	signatoryPhone?: string;
}

export interface GenerateDocumentResponse {
	documentUrl: string;
	documentId: number;
	fileId: number | string;
	message: string;
}

export interface SendDocumentForSignatureResponse {
	message: string;
	envelopeId?: string;
	trackingId?: string;
	taskId?: string;
	recipients?: {
		broker: string;
		dune: string;
	};
}

export const SignatureMethod = {
	ELECTRONIC: 'ELECTRONIC',
	MANUAL: 'MANUAL',
} as const;

export type SignatureMethodValues = typeof SignatureMethod[keyof typeof SignatureMethod];

export const TemplateAnchors = {
	CONVENTION_APPORTEUR: {
		required: ['#signataire1', '#signataire2'],
		signers: {
			apporteur: '#signataire1',
			dune: '#signataire2',
		}
	},
	PROPOSITION_ASSURANCE: {
		required: ['#signataire1', '#signataire2'],
		signers: {
			apporteur: '#signataire1',
			dune: '#signataire2',
		}
	},
	AVENANT_CONVENTION: {
		required: ['#signataire1', '#signataire2'],
		signers: {
			apporteur: '#signataire1',
			dune: '#signataire2',
		}
	},
	CONTRAT: {
		required: ['#signataire1', '#signataire2'],
		signers: {
			apporteur: '#signataire1',
			dune: '#signataire2',
		}
	},
} as const;

export type TemplateAnchorValues = typeof TemplateAnchors[keyof typeof TemplateAnchors];

export const DocumentFormat = {
	PDF: 'PDF',
	DOCX: 'DOCX',
} as const;

export type DocumentFormatValues = typeof DocumentFormat[keyof typeof DocumentFormat];

export type EntityTemplateMapping = Record<EntityModelValues, DocumentTemplateTypeValues[]>;

export type AttachableDocument = {
	id: string;
	name: string;
	bucket: string;
	key: string;
	mimeType: string;
	type: string;
	createdAt: Date;
	updatedAt: Date;
	status: string;
};

export type TemplateType = typeof templateTypes[number];


export type SendDocumentTypeConfig = {
	documentTypeFilter: string;
	labels: {
		title: {
			choice: string;
			manual: string;
			electronic: string;
		};
		description: {
			choice: string;
			manual: string;
			electronic: string;
		};
		selectLabel: string;
		selectPlaceholder: {
			loading: string;
			noDocuments: string;
		};
		primaryUploadLabel: string;
		secondaryUploadLabel: string;
		primaryDownloadLabel: string;
		secondaryDownloadLabel: string;
		primaryDocumentType: PropositionDocumentType;
		secondaryDocumentType: PropositionDocumentType | null
		submitButton: {
			manual: string;
			electronic: string;
		};
		docusignNotice: string;
	};
	errorMessages: {
		selectDocument: string;
		sendError: string;
	};
	successMessage: string;
}

export type GenerateDocumentTypeConfig = {
	title?: string;
	description?: string;
	templateLabel?: string;
	successMessage?: string;
	buttonLabel?: string;
	templateErrorMessage?: string;
	infoMessage?: (format: DocumentFormatValues) => string;
	noTemplateMessage?: (productType?: PropositionProductType) => string;
	getTemplateType?: (
		productType?: PropositionProductType,
	) => DocumentTemplateTypeValues | null;
}
export const SEND_DOCUMENT_TYPE_CONFIG: Record<ProjectDocumentForSignatureTypeKeys, SendDocumentTypeConfig> = {
	PROPOSITION: {
		documentTypeFilter: propositionDocumentTypeEnum.PROPOSITION_ASSURANCE,
		labels: {
			title: {
				choice: "Signature de la proposition",
				manual: "Signature manuelle",
				electronic: "Signature électronique",
			},
			description: {
				choice: "Merci de sélectionner le mode de signature",
				manual: "Veuillez importer les documents signés.",
				electronic: "Veuillez renseigner les informations du signataire",
			},
			selectLabel: "Proposition à envoyer",
			selectPlaceholder: {
				loading: "Chargement...",
				noDocuments:
					"Aucune proposition disponible. Veuillez d'abord générer une proposition.",
			},
			primaryUploadLabel: "Proposition d'assurance signée",
			secondaryUploadLabel: "Conditions générales signées",
			primaryDownloadLabel: "Proposition d'assurance",
			secondaryDownloadLabel: "Conditions générales",
			primaryDocumentType: propositionDocumentTypeEnum.PROPOSITION_ASSURANCE,
			secondaryDocumentType: propositionDocumentTypeEnum.CONDITIONS_GENERALES,
			submitButton: {
				manual: "Envoyer les documents",
				electronic: "Valider",
			},
			docusignNotice: "La proposition sera envoyée par Docusign.",
		},
		errorMessages: {
			selectDocument: "une proposition",
			sendError: "de la proposition",
		},
		successMessage: "Proposition envoyée",
	},
	CONTRACT: {
		documentTypeFilter: contractDocumentTypeEnum.CONTRAT,
		labels: {
			title: {
				choice: "Signature du contrat",
				manual: "Signature manuelle",
				electronic: "Signature électronique",
			},
			description: {
				choice: "Merci de sélectionner le mode de signature",
				manual: "Veuillez importer les documents signés.",
				electronic: "Veuillez renseigner les informations du signataire",
			},
			selectLabel: "Contrat à envoyer",
			selectPlaceholder: {
				loading: "Chargement...",
				noDocuments:
					"Aucun contrat disponible. Veuillez d'abord générer un contrat.",
			},
			primaryUploadLabel: "Contrat d'assurance signé",
			secondaryUploadLabel: "Conditions générales signées",
			primaryDownloadLabel: "Contrat d'assurance",
			secondaryDownloadLabel: "Conditions générales",
			primaryDocumentType: contractDocumentTypeEnum.CONTRAT,
			secondaryDocumentType: contractDocumentTypeEnum.CONDITIONS_GENERALES,
			submitButton: {
				manual: "Envoyer les documents",
				electronic: "Valider",
			},
			docusignNotice: "Le contrat sera envoyé par Docusign.",
		},
		errorMessages: {
			selectDocument: "un contrat",
			sendError: "du contrat",
		},
		successMessage: "Contrat envoyé",
	},
	END_OF_WORK_AMENDMENT: {
		documentTypeFilter: propositionDocumentTypeEnum.AVENANT_FIN_DE_TRAVAUX,
		labels: {
			title: {
				choice: "Signature de l'avenant fin de travaux",
				manual: "Signature manuelle",
				electronic: "Signature électronique",
			},
			description: {
				choice: "Merci de sélectionner le mode de signature",
				manual: "Veuillez importer le document signé.",
				electronic: "Veuillez renseigner les informations du signataire",
			},
			selectLabel: "Avenant à envoyer",
			selectPlaceholder: {
				loading: "Chargement...",
				noDocuments:
					"Aucun avenant fin de travaux disponible. Veuillez d'abord générer un avenant fin de travaux.",
			},
			primaryUploadLabel: "Avenant fin de travaux signé",
			secondaryUploadLabel: "",
			primaryDownloadLabel: "Avenant fin de travaux",
			secondaryDownloadLabel: "",
			primaryDocumentType: propositionDocumentTypeEnum.AVENANT_FIN_DE_TRAVAUX,
			secondaryDocumentType: null,
			submitButton: {
				manual: "Envoyer le document",
				electronic: "Valider",
			},
			docusignNotice: "L'avenant fin de travaux sera envoyé par Docusign.",
		},
		errorMessages: {
			selectDocument: "un avenant",
			sendError: "de l'avenant",
		},
		successMessage: "Avenant envoyé",
	},
}

export const GENERATE_DOCUMENT_TYPE_CONFIG: Record<ProjectDocumentGenerationTypeKeys, GenerateDocumentTypeConfig> = {
	CONTRACT: {
		title: "Générer un contrat",
		description:
			"Sélectionnez le modèle de contrat, le signataire Dune et le format de document",
		templateLabel: "Modèle de contrat",
		successMessage: "Contrat généré avec succès",
		buttonLabel: "Générer le contrat",
		templateErrorMessage: "Veuillez sélectionner un modèle de contrat",
		infoMessage: (format) =>
			`Le contrat sera généré au format ${format === DocumentFormat.PDF ? "PDF" : "Word"} et ajouté automatiquement aux documents du contrat.`,
		noTemplateMessage: (productType) =>
			productType
				? `Aucun modèle de contrat validé n'est disponible pour le type ${productType}.`
				: "Aucun modèle de contrat validé n'est disponible. Veuillez d'abord créer et valider un modèle.",
		getTemplateType: (productType) => {
			if (!productType) return null;
			const mapping: Record<PropositionProductType, DocumentTemplateTypeValues> = {
				DO: DocumentTemplateType.CONTRAT_ASSURANCE_DO,
				TRC:
					DocumentTemplateType.CONTRAT_ASSURANCE_TRC,
				MRT: DocumentTemplateType.CONTRAT_ASSURANCE_MRT,
				GFA: DocumentTemplateType.CONTRAT_ASSURANCE_GFA,
			};
			return mapping[productType];
		},
	},
	PROPOSITION: {
		title: "Générer une proposition d'assurance",
		description:
			"Sélectionnez le modèle de proposition, le signataire Dune et le format de document",
		templateLabel: "Modèle de proposition",
		successMessage: "Proposition générée avec succès",
		buttonLabel: "Générer la proposition",
		templateErrorMessage: "Veuillez sélectionner un modèle de proposition",
		infoMessage: (format) =>
			`La proposition sera générée au format ${format === DocumentFormat.PDF ? "PDF" : "Word"} et ajoutée automatiquement aux documents de la proposition.`,
		noTemplateMessage: (productType) =>
			productType
				? `Aucun modèle de proposition validé n'est disponible pour le type ${productType}.`
				: "Aucun modèle de proposition validé n'est disponible. Veuillez d'abord créer et valider un modèle.",
		getTemplateType: (productType) => {
			if (!productType) return null;
			const mapping: Record<PropositionProductType, DocumentTemplateTypeValues> = {
				DO: DocumentTemplateType.PROPOSITION_ASSURANCE_DO,
				TRC: DocumentTemplateType.PROPOSITION_ASSURANCE_TRC,
				MRT: DocumentTemplateType.PROPOSITION_ASSURANCE_MRT,
				GFA: DocumentTemplateType.PROPOSITION_ASSURANCE_GFA,
			};
			return mapping[productType];
		},
	},
	END_OF_WORK_AMENDMENT: {
		title: "Générer un avenant fin de travaux",
		description:
			"Sélectionnez le modèle de avenant, le signataire Dune et le format de document",
		templateLabel: "Modèle de avenant",
		successMessage: "Avenant fin de travaux généré avec succès",
		buttonLabel: "Générer l'avenant",
		getTemplateType: (productType) => {
			if (!productType) return null;
			const mapping: Record<PropositionProductType, DocumentTemplateTypeValues> = {
				DO: DocumentTemplateType.AVENANT_FIN_DE_TRAVAUX_DO,
				TRC: DocumentTemplateType.AVENANT_FIN_DE_TRAVAUX_TRC,
				MRT: DocumentTemplateType.AVENANT_FIN_DE_TRAVAUX_MRT,
				GFA: DocumentTemplateType.AVENANT_FIN_DE_TRAVAUX_GFA,
			};
			return mapping[productType];
		},
	},
	FINAL_CERTIFICATE: {
		title: "Générer une attestation définitive",
		description:
			"Sélectionnez le modèle d'attestation définitive, le signataire Dune et le format de document",
		templateLabel: "Modèle d'attestation définitive",
		successMessage: "Attestation définitive générée avec succès",
		buttonLabel: "Générer l'attestation définitive",
		getTemplateType: (productType) => {
			if (!productType) return null;
			const mapping: Record<PropositionProductType, DocumentTemplateTypeValues> = {
				DO: DocumentTemplateType.ATTESTATION_DEFINITIVE_DO,
				TRC: DocumentTemplateType.ATTESTATION_DEFINITIVE_TRC,
				MRT: DocumentTemplateType.ATTESTATION_DEFINITIVE_MRT,
				GFA: DocumentTemplateType.ATTESTATION_DEFINITIVE_GFA,
			};
			return mapping[productType];
		},
	},
	QUITTANCE: {
		title: "Générer une quittance",
		description:
			"Sélectionnez le modèle de quittance, la quittance et le format de document",
		templateLabel: "Modèle de quittance",
		successMessage: "Quittance générée avec succès",
		buttonLabel: "Générer la quittance",
		templateErrorMessage: "Veuillez sélectionner un modèle de quittance",
		getTemplateType: () => DocumentTemplateType.QUITTANCE,
	},
};

export const GFA_GENERATE_DOCUMENT_TYPE_CONFIG: Record<GFAProjectDocumentGenerationTypeKeys, GenerateDocumentTypeConfig> = {
	...GENERATE_DOCUMENT_TYPE_CONFIG,
	DEVIS_GFA: {
		title: "Générer un devis GFA",
		description:
			"Sélectionnez le modèle de devis GFA, le signataire Dune et le format de document",
		templateLabel: "Modèle de devis GFA",
		successMessage: "Devis GFA généré avec succès",
		buttonLabel: "Générer le devis GFA",
		templateErrorMessage: "Veuillez sélectionner un modèle de devis GFA",
		getTemplateType: () => DocumentTemplateType.DEVIS_GFA,
	},
	RAPPORT_EXPERTISE_GFA: {
		title: "Générer un rapport d'expertise GFA",
		description:
			"Sélectionnez le modèle de rapport d'expertise GFA, le signataire Dune et le format de document",
		templateLabel: "Modèle de rapport d'expertise GFA",
		successMessage: "Rapport d'expertise GFA généré avec succès",
		buttonLabel: "Générer le rapport d'expertise GFA",
		templateErrorMessage: "Veuillez sélectionner un modèle de rapport d'expertise GFA",
		getTemplateType: () => DocumentTemplateType.RAPPORT_EXPERTISE_GFA,
	},
	INDICATION_TARIFAIRE_GFA_CARAVELA: {
		title: "Générer une indication tarifaire GFA Caravela",
		description:
			"Sélectionnez le modèle d'indication tarifaire GFA Caravela, le signataire Dune et le format de document",
		templateLabel: "Modèle d'indication tarifaire GFA Caravela",
		successMessage: "Indication tarifaire GFA Caravela générée avec succès",
		buttonLabel: "Générer l'indication tarifaire GFA Caravela",
		templateErrorMessage: "Veuillez sélectionner un modèle d'indication tarifaire GFA Caravela",
		getTemplateType: () => DocumentTemplateType.INDICATION_TARIFAIRE_GFA_CARAVELA,
	},
};

export const DOCUSIGN_DOCUMENT_TYPE_MAPPING: Record<
ProjectDocumentForSignatureTypeKeys,
{
	prismaType: PropositionDocumentType;
	emailSubject: string;
	labels: {
		article: { definite: string; indefinite: string };
		preposition: string;
		label: string;
	};
	feminine: boolean;
}
> = {
	PROPOSITION: {
		prismaType: "PROPOSITION_ASSURANCE",
		emailSubject: "Proposition d'assurance",
		labels: {
			article: {
				definite: "La proposition",
				indefinite: "Cette proposition",
			},
			preposition: "de la proposition",
			label: "Proposition",
		},
		feminine: true,
	},
	CONTRACT: {
		prismaType: "CONTRAT",
		emailSubject: "Contrat d'assurance",
		labels: {
			article: {
				definite: "Le contrat",
				indefinite: "Ce contrat",
			},
			preposition: "du contrat",
			label: "Contrat",
		},
		feminine: false,
	},
	END_OF_WORK_AMENDMENT: {
		prismaType: "AVENANT_FIN_DE_TRAVAUX",
		emailSubject: "Avenant fin de travaux",
		labels: {
			article: {
				definite: "L'avenant fin de travaux",
				indefinite: "Cet avenant fin de travaux",
			},
			preposition: "de l'avenant fin de travaux",
			label: "Avenant fin de travaux",
		},
		feminine: false,
	},
};